import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * Full-resolution image + video lightbox.
 *
 * Design goals (PRD § Phase 4 UI):
 *   • Load the full-res source only when opened (deferred until user
 *     asks — cheaper page loads).
 *   • Desktop: mouse wheel zoom, buttons for zoom in/out/reset, pan
 *     via click-drag when zoomed. ESC to close.
 *   • Touch: pinch-to-zoom via two-finger gesture; single-finger drag
 *     to pan when zoomed.
 *   • Video: standard HTML controls, plays with sound (only the
 *     inline hero preview is muted).
 *   • Semi-opaque scrim over ink-bg; ESC / backdrop click closes.
 */
export default function Lightbox({ open, onClose, item }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  return (
    <div
      data-testid="lightbox"
      onClick={onClose}
      className="animate-fade fixed inset-0 z-[70] flex items-center justify-center bg-ink-bg/95 p-4"
    >
      <button
        type="button"
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="fixed right-4 top-4 z-[71] rounded-chip border border-ink-chip bg-ink-raised p-2 text-ink-muted hover:text-ink-text"
      >
        <X size={18} />
      </button>

      <div onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full">
        {item.kind === "video" ? (
          <VideoView item={item} />
        ) : (
          <ImageView item={item} />
        )}
      </div>
    </div>
  );
}

function VideoView({ item }) {
  return (
    <video
      data-testid="lightbox-video"
      src={item.src}
      poster={item.poster}
      controls
      autoPlay
      className="max-h-[90vh] max-w-[92vw] rounded-card"
    />
  );
}

/**
 * Zoomable image via CSS transform. Wheel / pinch adjusts scale;
 * click-drag pans when scale > 1.
 */
function ImageView({ item }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  const clampScale = useCallback((s) => Math.min(6, Math.max(1, s)), []);

  function reset() {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }

  // Wheel to zoom (desktop)
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setScale((s) => clampScale(s - e.deltaY * 0.002));
  }, [clampScale]);

  // Click-drag to pan
  const onPointerDown = (e) => {
    if (scale <= 1) return;
    if (e.pointerType === "touch") return; // touch handled via pinch below
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onPointerMove = (e) => {
    if (dragRef.current) {
      setPan({
        x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
        y: dragRef.current.panY + (e.clientY - dragRef.current.startY),
      });
    }
  };
  const onPointerUp = () => { dragRef.current = null; };

  // Pinch on touch — track two active pointers.
  const activePointers = useRef(new Map());
  const onTouchPointerDown = (e) => {
    activePointers.current.set(e.pointerId,
      { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 2) {
      const [a, b] = Array.from(activePointers.current.values());
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), startScale: scale };
    }
  };
  const onTouchPointerMove = (e) => {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 2 && pinchRef.current) {
      const [a, b] = Array.from(activePointers.current.values());
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const factor = d / pinchRef.current.dist;
      setScale(clampScale(pinchRef.current.startScale * factor));
    }
  };
  const onTouchPointerUp = (e) => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinchRef.current = null;
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={wrapRef}
        data-testid="lightbox-canvas"
        className="relative flex max-h-[85vh] max-w-[92vw] items-center justify-center overflow-hidden rounded-card bg-black"
        onPointerDown={(e) => { onPointerDown(e); onTouchPointerDown(e); }}
        onPointerMove={(e) => { onPointerMove(e); onTouchPointerMove(e); }}
        onPointerUp={(e) => { onPointerUp(); onTouchPointerUp(e); }}
        onPointerCancel={(e) => { onPointerUp(); onTouchPointerUp(e); }}
        style={{ touchAction: scale > 1 ? "none" : "auto" }}
      >
        <img
          data-testid="lightbox-image"
          src={item.src}
          alt=""
          draggable={false}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: dragRef.current || pinchRef.current
              ? "none"
              : "transform 120ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
          className="max-h-[85vh] max-w-[92vw] select-none object-contain"
        />
      </div>
      <div className="flex items-center gap-1.5">
        <ZoomBtn onClick={() => setScale((s) => clampScale(s - 0.25))} testId="lightbox-zoom-out" ariaLabel="Zoom out">
          <ZoomOut size={16} />
        </ZoomBtn>
        <div className="min-w-[52px] rounded-chip border border-ink-chip bg-ink-raised px-2 py-1 text-center text-meta text-ink-muted tabular" data-testid="lightbox-scale">
          {Math.round(scale * 100)}%
        </div>
        <ZoomBtn onClick={() => setScale((s) => clampScale(s + 0.25))} testId="lightbox-zoom-in" ariaLabel="Zoom in">
          <ZoomIn size={16} />
        </ZoomBtn>
        <ZoomBtn onClick={reset} testId="lightbox-zoom-reset" ariaLabel="Reset zoom">
          <RotateCcw size={16} />
        </ZoomBtn>
      </div>
    </div>
  );
}

function ZoomBtn({ children, onClick, testId, ariaLabel }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      className="inline-flex h-8 w-8 items-center justify-center rounded-chip border border-ink-chip bg-ink-raised text-ink-muted hover:border-ink-muted hover:text-ink-text"
    >
      {children}
    </button>
  );
}
